import { useEffect, useState } from "react";
import { Activity, Database, Download, HardDrive, Mail, CreditCard, Images, Server, RefreshCw, AlertTriangle, ToggleLeft, ToggleRight, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const CHECK_META = {
  mongodb: { label: "Base de données MongoDB", icon: Database },
  api: { label: "API FastAPI", icon: Server },
  stripe: { label: "Paiements Stripe", icon: CreditCard },
  emails: { label: "E-mails transactionnels", icon: Mail },
  mediatheque: { label: "Médiathèque (stockage objet)", icon: Images },
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function ParametresPage() {
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState(null);
  const [logs, setLogs] = useState(null);
  const [tab, setTab] = useState("sante");
  const [backingUp, setBackingUp] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadHealth = () => api.get("/admin/system/health").then((r) => setHealth(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadBackups = () => api.get("/admin/system/backups").then((r) => setBackups(r.data)).catch(() => setBackups([]));
  const loadLogs = () => api.get("/admin/system/logs", { params: { lines: 200 } }).then((r) => setLogs(r.data)).catch(() => setLogs(null));
  useEffect(() => { loadHealth(); loadBackups(); }, []);

  const runBackup = async () => {
    setBackingUp(true);
    try {
      const r = await api.post("/admin/system/backup");
      toast.success(`Sauvegarde créée : ${r.data.filename} (${formatSize(r.data.size_bytes)})`);
      loadBackups();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBackingUp(false); }
  };

  const toggleMaintenance = async () => {
    const next = !health?.maintenance_mode;
    if (next && !window.confirm("Activer le mode maintenance ? Le site public ET l'espace client afficheront une page d'attente. L'administration reste accessible.")) return;
    setToggling(true);
    try {
      await api.post("/admin/system/maintenance", { enabled: next });
      toast.success(next ? "Mode maintenance ACTIVÉ — le site public affiche la page d'attente" : "Mode maintenance désactivé — le site est de nouveau en ligne");
      loadHealth();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setToggling(false); }
  };

  if (!health) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="parametres-loading">Chargement…</div>;

  const TABS = [["sante", "Santé système"], ["sauvegardes", "Sauvegardes"], ["logs", "Journal d'erreurs"]];

  return (
    <div data-testid="admin-parametres-page">
      <p className="overline-ae">B23 — Paramètres techniques</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Paramètres techniques &amp; infrastructure</h1>
      <p className="mt-1 text-sm text-muted-foreground">Supervision du système — réservé à l'administrateur principal.</p>

      <div className={`mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5 ${health.maintenance_mode ? "border-gold/60 bg-gold-light" : "border-border bg-white"}`} data-testid="maintenance-card">
        <div>
          <p className="font-heading text-lg text-deepgreen">Mode maintenance</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {health.maintenance_mode
              ? "ACTIVÉ — le site public et l'espace client affichent la page d'attente. L'administration reste accessible."
              : "Désactivé — le site est en ligne."}
          </p>
        </div>
        <button type="button" onClick={toggleMaintenance} disabled={toggling} className={health.maintenance_mode ? "btn-primary-ae !px-5 !py-2.5 !text-xs" : "btn-secondary-ae !px-5 !py-2.5 !text-xs"} data-testid="maintenance-toggle">
          {health.maintenance_mode ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          {toggling ? "Bascule…" : health.maintenance_mode ? "Désactiver la maintenance" : "Activer la maintenance"}
        </button>
      </div>

      <div className="mt-6 flex gap-2 border-b border-border">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => { setTab(id); if (id === "logs" && !logs) loadLogs(); }} data-testid={`parametres-tab-${id}`}
            className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "border-deepgreen text-deepgreen" : "border-transparent text-muted-foreground hover:text-deepgreen"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "sante" && (
        <div className="mt-6" data-testid="health-panel">
          <div className={`mb-4 flex items-center gap-3 rounded-lg p-4 text-sm font-semibold ${health.overall === "ok" ? "bg-sage-light text-deepgreen" : "bg-gold-light text-deepgreen"}`} data-testid="health-overall">
            <Activity className="h-5 w-5" />
            {health.overall === "ok" ? "Tous les systèmes sont opérationnels" : "Un ou plusieurs services demandent votre attention"}
            <button type="button" onClick={loadHealth} className="ml-auto btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid="health-refresh"><RefreshCw className="h-3.5 w-3.5" /> Actualiser</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Object.entries(CHECK_META).map(([key, meta]) => {
              const check = health.checks[key] || { status: "error", detail: "Inconnu" };
              const ok = check.status === "ok";
              return (
                <div key={key} className="card-ae p-5" data-testid={`health-${key}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <meta.icon className="h-5 w-5 text-sage" />
                      <p className="text-sm font-bold text-deepgreen">{meta.label}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${ok ? "bg-sage-light text-deepgreen" : "bg-red-50 text-red-600"}`} data-testid={`health-${key}-status`}>
                      {ok ? "OK" : "Erreur"}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{check.detail}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-6 rounded-lg bg-sage-light p-4 text-xs text-muted-foreground" data-testid="dns-note">
            <p className="font-bold text-deepgreen">Délivrabilité e-mails (SPF / DKIM / DMARC)</p>
            <p className="mt-1">Ces enregistrements DNS se configurent chez votre registrar (art-events.fr) depuis le tableau de bord de votre fournisseur d'e-mails (Resend → Domaines). La vérification SPF/DKIM/DMARC s'y fait en un clic ; ce panneau confirme ensuite que l'envoi fonctionne.</p>
          </div>
        </div>
      )}

      {tab === "sauvegardes" && (
        <div className="mt-6" data-testid="backups-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Dumps MongoDB stockés sur le volume persistant <code className="rounded bg-cream px-1.5 py-0.5 text-xs">/uploads/backups</code> — rotation automatique à 30 jours.</p>
            <button type="button" onClick={runBackup} disabled={backingUp} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="backup-run">
              <HardDrive className="h-4 w-4" /> {backingUp ? "Sauvegarde en cours…" : "Lancer une sauvegarde maintenant"}
            </button>
          </div>
          {!backups || backups.length === 0 ? (
            <div className="mt-4"><EmptyState icon={Database} title="Aucune sauvegarde">Lancez la première sauvegarde manuelle de la base.</EmptyState></div>
          ) : (
            <div className="card-ae mt-4 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm" data-testid="backups-table">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3">Fichier</th><th className="px-5 py-3">Taille</th><th className="px-5 py-3">Statut</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Par</th>
                </tr></thead>
                <tbody className="divide-y divide-border">
                  {backups.map((b) => (
                    <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                      <td className="px-5 py-3 font-mono text-xs text-deepgreen">{b.filename}</td>
                      <td className="px-5 py-3">{formatSize(b.size_bytes)}</td>
                      <td className="px-5 py-3">{b.status === "ok" ? <span className="rounded-full bg-sage-light px-2.5 py-1 text-[10px] font-bold text-deepgreen">Réussie</span> : <span className="rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-600" title={b.error || ""}>Erreur</span>}</td>
                      <td className="px-5 py-3 text-muted-foreground">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-5 py-3 text-muted-foreground">{b.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "logs" && (
        <div className="mt-6" data-testid="logs-panel">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground flex items-center gap-2"><ScrollText className="h-4 w-4" /> Journal : <code className="rounded bg-cream px-1.5 py-0.5 text-xs">{logs?.source || "…"}</code></p>
            <button type="button" onClick={loadLogs} className="btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid="logs-refresh"><RefreshCw className="h-3.5 w-3.5" /> Actualiser</button>
          </div>
          {!logs ? (
            <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
          ) : logs.lines.length === 0 ? (
            <div className="mt-4"><EmptyState icon={AlertTriangle} title="Aucune erreur récente">{logs.note || "Le journal est vide — le système tourne proprement."}</EmptyState></div>
          ) : (
            <pre className="card-ae mt-4 max-h-[480px] overflow-auto whitespace-pre-wrap break-all p-4 font-mono text-[11px] leading-relaxed text-deepgreen/90" data-testid="logs-content">{logs.lines.join("\n")}</pre>
          )}
          <p className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground"><Download className="h-3.5 w-3.5" /> Le journal complet est accessible sur le serveur (chemin configurable via APP_ERROR_LOG).</p>
        </div>
      )}
    </div>
  );
}
